import { useQuery } from "@tanstack/react-query";

import type { FilterSpec } from "@/components/data";

import { getCriteria, getFilterValues, STALE_TIME, type FilterDimension } from "./api";

/**
 * The option lists behind every distribution filter (C6-b).
 *
 * One query key for both tabs, so opening Leads and then Opportunities costs one
 * fetch rather than two. Which endpoint the options come from, and how long they
 * are held, is this hook's business and nobody else's.
 */
export function useFilterValues() {
  return useQuery({ queryKey: ["distribution", "filter-values"], queryFn: () => getFilterValues(), staleTime: STALE_TIME });
}

/** The scoring criteria and the facts each one requires (C6-e). */
export function useCriteria() {
  return useQuery({ queryKey: ["distribution", "criteria"], queryFn: () => getCriteria(), staleTime: STALE_TIME });
}

/** What a filter says for itself when its options could not be loaded. */
export const OPTIONS_UNAVAILABLE = "Filter options unavailable — retry";

/**
 * Build one pick-list from a `/filter-values` dimension.
 *
 * On error the pill renders DISABLED rather than falling back to a hardcoded
 * list: a stale option list looks identical to a live one and quietly hides
 * whole markets, which is worse than a control that is visibly out of order.
 */
export function choiceSpec(
  name: string,
  label: string,
  dimension: FilterDimension | undefined,
  { anyLabel = "Any", isError = false }: { anyLabel?: string; isError?: boolean } = {},
): FilterSpec {
  return { name, label, kind: "choice", anyLabel, options: dimension?.values ?? [], disabled: isError };
}

/**
 * The date filter is a CLOSED list of relative periods, not a data dimension —
 * "this month" is not a value any lead holds — so it is declared here and the
 * boundary is computed at query time. A fixed list of periods covers the stated
 * need with one pill and no date-picker dependency (intentional debt: an
 * arbitrary range is a later change if an operator asks for a specific window).
 */
export const DATE_PERIODS = [
  { value: "this_week", label: "This week" },
  { value: "this_month", label: "This month" },
  { value: "last_30", label: "Last 30 days" },
] as const;
export type DatePeriod = (typeof DATE_PERIODS)[number]["value"];

// Built from LOCAL date parts. `toISOString()` would convert the operator's local midnight to
// UTC and hand the engine the previous day east of Greenwich, so "this month" would quietly
// start on the 31st of the month before.
const iso = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/** Map a relative period onto the C2 `created_since` / `created_before` params. */
export function datePeriodParams(period: string | null | undefined, now = new Date()): { created_since?: string } {
  if (!period) return {};
  if (period === "this_week") {
    const start = new Date(now);
    // ISO weeks start Monday; getDay() calls Sunday 0, so Sunday walks back 6 days.
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
    return { created_since: iso(start) };
  }
  if (period === "this_month") return { created_since: iso(new Date(now.getFullYear(), now.getMonth(), 1)) };
  if (period === "last_30") {
    const start = new Date(now);
    start.setDate(start.getDate() - 30);
    return { created_since: iso(start) };
  }
  return {};
}

/** The one-line summary of what is currently narrowing the view, for the empty state. */
export function activeFilterSummary(specs: FilterSpec[], values: Record<string, unknown>): string {
  return specs
    .map((spec) => {
      const value = values[spec.name];
      if (typeof value !== "string" || !value) return null;
      const option = (spec.options ?? []).find((item) => item.value === value);
      return `${spec.label}: ${option?.label ?? value}`;
    })
    .filter((part): part is string => part !== null)
    .join(" · ");
}
